import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  Ban,
  CircleCheck,
  Clock,
  FileText,
  Plus,
  TriangleAlert,
} from 'lucide-react'
import { type ReactNode } from 'react'

import {
  templateHeaderFormat,
  type TemplateHeaderFormat,
} from '@aeris/communication'
import {
  Badge,
  Button,
  EmptyState,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  type StatusTone,
} from '@aeris/ui'

import { TabHeader } from '../components/config-tabs.tsx'
import { fetchTemplates, type TemplateSummaryType } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'

const MEDIA_HEADER_LABEL: Record<TemplateHeaderFormat, string> = {
  TEXT: 'Texto',
  IMAGE: 'Imagem',
  VIDEO: 'Vídeo',
  DOCUMENT: 'Documento',
}

/**
 * Atendimento → Modelos: os modelos aprovados pela Meta (F050), e a lista
 * que leva até o formulário de criação (F059).
 *
 * A F046 já bloqueava o envio livre fora da janela de 24h dizendo "só um
 * modelo aprovado pode ser enviado agora" — sem nenhum modelo existir ainda.
 * Esta tela é o que faz um modelo existir: categoria e situação em
 * DESTAQUE, porque é a categoria que decide o custo (o rework de cobrança da
 * Meta em 01/10/2026) e é a situação que decide se um atendente pode
 * escolher este modelo no inbox.
 *
 * **A criação deixou de ser um modal (F059).** Um formulário que decide como
 * o cliente vai LER a mensagem — texto, negrito, botões — precisa de espaço
 * para mostrar isso, não de uma caixa de 32rem por cima da lista. A criação
 * mora em `/atendimento-modelos/novo` (`atendimento-modelos-novo.tsx`), uma
 * sub-rota que `moduleForPath` resolve pelo prefixo mais longo
 * (`lib/workspace.ts`) — a mesma tela desta lista continua sendo o módulo.
 *
 * **A aprovação leva DIAS, não minutos.** O formulário de criação diz isso
 * com todas as letras — quem cria um modelo hoje não envia uma campanha
 * amanhã.
 */

const CATEGORY_LABELS: Record<string, string> = {
  MARKETING: 'Marketing',
  UTILITY: 'Utilidade',
  AUTHENTICATION: 'Autenticação',
}

const CATEGORY_TONES: Record<string, StatusTone> = {
  MARKETING: 'info',
  UTILITY: 'success',
  AUTHENTICATION: 'neutral',
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Em análise',
  approved: 'Aprovado',
  rejected: 'Recusado',
  paused: 'Pausado',
  disabled: 'Desativado',
}

const STATUS_TONES: Record<string, StatusTone> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
  paused: 'warning',
  disabled: 'neutral',
}

const STATUS_ICONS: Record<string, ReactNode> = {
  pending: <Clock aria-hidden="true" className="h-3 w-3" />,
  approved: <CircleCheck aria-hidden="true" className="h-3 w-3" />,
  rejected: <Ban aria-hidden="true" className="h-3 w-3" />,
  paused: <TriangleAlert aria-hidden="true" className="h-3 w-3" />,
  disabled: <Ban aria-hidden="true" className="h-3 w-3" />,
}

const StatusBadge = ({ status }: { status: string }): ReactNode => (
  <Badge tone={STATUS_TONES[status] ?? 'neutral'} icon={STATUS_ICONS[status]}>
    {STATUS_LABELS[status] ?? status}
  </Badge>
)

const CategoryBadge = ({ category }: { category: string }): ReactNode => (
  <Badge tone={CATEGORY_TONES[category] ?? 'neutral'}>
    {CATEGORY_LABELS[category] ?? category}
  </Badge>
)

export const AtendimentoModelosPage = (): ReactNode => {
  const { allows } = useAuth()
  const canManage = allows('communication.template.manage')

  const templates = useQuery({
    queryKey: ['communication-templates'],
    queryFn: async ({ signal }) => await fetchTemplates(undefined, signal),
  })

  const rows = templates.data ?? []

  return (
    <>
      <TabHeader
        title="Modelos"
        description="Modelos aprovados pela Meta — fora da janela de 24 horas, é a única coisa que o Aeris pode enviar."
        actions={
          canManage ? (
            <Button asChild>
              <Link to="/atendimento-modelos/novo">
                <Plus aria-hidden="true" /> Novo modelo
              </Link>
            </Button>
          ) : null
        }
      />

      {templates.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : templates.isError ? (
        <EmptyState
          icon={<FileText aria-hidden="true" className="h-8 w-8" />}
          title="Não foi possível carregar os modelos"
          description="Verifique a conexão com o servidor e tente novamente."
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<FileText aria-hidden="true" className="h-8 w-8" />}
          title="Nenhum modelo cadastrado"
          description={
            canManage
              ? 'Crie um modelo para poder responder fora da janela de 24 horas.'
              : 'Peça a um administrador para cadastrar um modelo aprovado pela Meta.'
          }
        />
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Modelo</TableHeaderCell>
                <TableHeaderCell>Categoria</TableHeaderCell>
                <TableHeaderCell>Situação</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((template: TemplateSummaryType) => (
                <TableRow key={template.id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-(--weight-medium)">
                        {template.metaTemplateName}
                      </span>
                      <span className="text-(length:--text-xs) text-(--text-muted)">
                        {template.language}
                        {/*
                          F066 — um modelo com cabeçalho de mídia não pode
                          ser enviado por aqui ainda (o gateway não sobe
                          arquivo dentro de `components`), e o seletor do
                          inbox o esconde. Dizer POR QUE ele não aparece lá é
                          melhor que deixar a pessoa procurar.
                        */}
                        {templateHeaderFormat(template.structure) === 'TEXT'
                          ? null
                          : ` · ${MEDIA_HEADER_LABEL[templateHeaderFormat(template.structure)]} no cabeçalho — ainda não enviável pelo Aeris`}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <CategoryBadge category={template.category} />
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <StatusBadge status={template.status} />
                      {template.status === 'rejected' &&
                      template.rejectionReason !== null ? (
                        <span className="text-(length:--text-xs) text-(--text-muted)">
                          {template.rejectionReason}
                        </span>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  )
}
